/**
 * DTC adapter boundary for the SLD device card.
 *
 * The render layer only ever sees `DtcEntry[]`. Two adapters feed it:
 * `fromStructuredDtc` (preferred, Demsv1.0#2153) and `fromDtcText` (today's
 * lossy prose fallback). Swapping which one fires must not require component
 * changes. See design doc §7.2.
 */
import type { ActiveAlarm } from '@/types/alarm.types';

export type DtcGrounding = 'oem' | 'sae' | 'grounded' | 'generic';

export interface DtcEntry {
  spn: number;
  fmi: number;
  oc: number;
  description: string;
  grounding: DtcGrounding;
  engineMake?: string;
  engineModel?: string;
}

export interface DtcParseResult {
  entries: DtcEntry[];
  /** metadata.count as a number, or null when absent/non-numeric. */
  declaredCount: number | null;
  /** Raw `value` when neither adapter applied — surfaced rather than dropped. */
  rawFallback: string | null;
}

/**
 * Prefix test, not equality. Only `gen.dtc.code` reaches the cloud today, but
 * `gen.dtc.previous_codes` and the count conditions are siblings that must land
 * in the DTC section rather than leaking into Warnings if they are ever
 * promoted upstream. Design doc §6.
 */
export function isDtcCondition(key: string): boolean {
  return key.startsWith('gen.dtc.');
}

const TRIPLE = /SPN:(\d+)\/FMI:(\d+)\/OC:(\d+)/g;

/**
 * Both of genieos's generic fallback forms start `SPN <n> (` — either
 * `(engine-specific)` or `(<family>-specific, see engine ECM manual)`. Anchor on
 * the family-agnostic prefix: matching a family name would put an OEM string in
 * cloud code and break as new OEMs are added. Design doc §7.4.
 */
function groundingFromText(description: string): DtcGrounding {
  return /^SPN \d+ \(/.test(description) ? 'generic' : 'grounded';
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Parse the annotated prose form: `SPN:n/FMI:n/OC:n (desc); SPN:...`.
 *
 * Anchors on the code token and peels a fixed wrapper at known offsets, rather
 * than splitting on separators — descriptions contain semicolons, nested
 * parentheses and escaped quotes, so any separator-based split corrupts them.
 * Peel order matters: the byte sequence is `…desc); `, so `"; "` must come off
 * before `")"`. Design doc §7.3.
 *
 * TRIPLE matches a code-shaped token anywhere in the string, including inside
 * description prose (descriptions are free-form upstream text and are not
 * guaranteed to stay free of that shape). A real code only ever starts the
 * string or follows the `"; "` entry separator, so raw matches are filtered
 * to those two positions before span math runs — an in-description token is
 * left untouched as part of the surrounding description text rather than
 * being sliced out as its own entry.
 */
function fromDtcText(text: string, make?: string, model?: string): DtcEntry[] {
  const matches = [...text.matchAll(TRIPLE)].filter((m) => {
    const idx = m.index ?? 0;
    return idx === 0 || text.slice(idx - 2, idx) === '; ';
  });
  return matches.map((m, i) => {
    const start = (m.index ?? 0) + m[0].length;
    const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
    let seg = text.slice(start, end);

    if (seg.startsWith(' (')) seg = seg.slice(2);
    if (seg.endsWith('; ')) seg = seg.slice(0, -2);
    else if (seg.endsWith(';')) seg = seg.slice(0, -1);
    if (seg.endsWith(')')) seg = seg.slice(0, -1);

    const description = seg.trim();
    return {
      spn: Number(m[1]),
      fmi: Number(m[2]),
      oc: Number(m[3]),
      description,
      grounding: groundingFromText(description),
      engineMake: make,
      engineModel: model,
    };
  });
}

const VALID_GROUNDING = new Set<DtcGrounding>(['oem', 'sae', 'grounded', 'generic']);

/**
 * Parse the structured form (Demsv1.0#2153). `grounding` is read directly
 * rather than inferred, which is the whole point of the wire change — through
 * the prose adapter, 'oem' is undecidable.
 *
 * Returns `null` when the payload could not be read as a structured array at
 * all (malformed JSON, or JSON that parses to a non-array) — the caller falls
 * through to the prose/raw fallbacks in that case.
 *
 * Also returns `null` when the array was non-empty but NONE of its items
 * survived validation (missing/non-finite spn/fmi/oc — e.g. a field-name
 * drift upstream). A declared-but-wholly-unusable payload is the same
 * "unreadable" case as malformed JSON, not a "zero codes" answer, and must
 * fall through the same way — otherwise a perfectly good `dtc_text` fallback
 * is discarded in favor of a silently-empty result that reads as "0 active
 * codes" when the truth is "could not read this."
 *
 * `metadata.dtc: "[]"` — an array that was empty BEFORE validation — is the
 * one case that legitimately returns `[]`: a declared, structured "zero
 * codes" answer from the preferred adapter. Caller relies on that
 * distinction (via `[]` being truthy) to stop there rather than falling
 * through. Conflating the two used to make a valid empty array silently
 * defer to `dtc_text`, printing a stale or unrelated prose description
 * instead of "0 active codes" — the fix for that must not be re-broken by
 * routing an all-unusable array down the same "stop here" path.
 */
function fromStructuredDtc(raw: string, make?: string, model?: string): DtcEntry[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) return [];

  const out: DtcEntry[] = [];
  for (const item of parsed) {
    if (typeof item !== 'object' || item === null) continue;
    const o = item as Record<string, unknown>;
    const spn = Number(o.spn);
    const fmi = Number(o.fmi);
    const oc = Number(o.oc);
    if (!Number.isFinite(spn) || !Number.isFinite(fmi) || !Number.isFinite(oc)) continue;
    const description = str(o.text) ?? '';
    const g = o.grounding as DtcGrounding;
    out.push({
      spn, fmi, oc, description,
      grounding: VALID_GROUNDING.has(g) ? g : groundingFromText(description),
      engineMake: make,
      engineModel: model,
    });
  }
  // Non-empty input, but nothing in it was usable: treat as unreadable so the
  // caller falls through to dtc_text/raw, rather than presenting a false "0
  // active codes".
  return out.length === 0 ? null : out;
}

/**
 * Adapter precedence: structured -> prose -> raw `value` escape hatch.
 *
 * `metadata.count` arrives as a STRING — Condition.Metadata is map[string]string
 * upstream and the normalizer copies string values only. Comparing against the
 * raw value would be true on every row. Design doc §7.3.
 */
export function parseDtcAlarm(alarm: ActiveAlarm): DtcParseResult {
  const md = (alarm.metadata ?? {}) as Record<string, unknown>;
  const make = str(md.engine_make);
  const model = str(md.engine_model);

  // Number('') and Number('   ') both coerce to 0, so an empty/whitespace-only
  // count must be normalized to '' before the numeric check or it silently
  // becomes 0 instead of the documented null.
  const countInput = typeof md.count === 'string' ? md.count.trim() : md.count;
  const rawCount = countInput === '' ? NaN : Number(countInput);
  const declaredCount = Number.isFinite(rawCount) ? rawCount : null;

  const structuredRaw = str(md.dtc);
  if (structuredRaw) {
    const structured = fromStructuredDtc(structuredRaw, make, model);
    if (structured) return { entries: structured, declaredCount, rawFallback: null };
  }

  const text = str(md.dtc_text);
  if (text) {
    const entries = fromDtcText(text, make, model);
    // I3: dtc_text being PRESENT but parsing to zero entries (format drift,
    // a stray leading space before the first code token, anything the TRIPLE
    // regex doesn't match) must not silently present as "0 active codes" —
    // that is indistinguishable from a genuinely healthy device. Surface the
    // raw text as the escape hatch instead, exactly as if no adapter had
    // matched at all. Only a SUCCESSFUL parse clears rawFallback.
    return { entries, declaredCount, rawFallback: entries.length === 0 ? text : null };
  }

  return { entries: [], declaredCount, rawFallback: alarm.value ?? null };
}
